/**
 * As contas e as palavras da tela de medições. Fora do componente, porque é o
 * que dá para provar sem montar React.
 */
import type { Medicao, SituacaoDaMedicao } from './api';

/**
 * A situação escrita, e por que ela NÃO é um selo de status.
 *
 * O manual (p.19, R02 p.31) manda desenhar status em selo preenchido, e lista
 * seis nomes — `Operando`, `Standby`, `Parada`, `Em manutenção`, `Disponível`,
 * `Em deslocamento`. Todos os seis descrevem uma MÁQUINA. Uma medição não está
 * "Operando", e pintar `aprovada` de verde-Operando emprestaria a cor de um
 * significado para outro: na mesma tela, verde passaria a querer dizer duas
 * coisas.
 *
 * Criar um sétimo conjunto de cores seria inventar norma, e o `CLAUDE.md` é
 * explícito sobre isso. Então a situação sai como TEXTO escrito, que é o mesmo
 * caminho que a tela de ordens já tomou para `ordem.situacao`. O rótulo
 * escrito era, de qualquer forma, a parte obrigatória da regra.
 */
export function situacaoEscrita(situacao: SituacaoDaMedicao): string {
  if (situacao === 'aberta') return 'Aberta';
  if (situacao === 'em conferencia') return 'Em conferência';
  if (situacao === 'aprovada') return 'Aprovada';
  return 'Cancelada';
}

export interface TotaisDaReceita {
  /** Só o que foi APROVADO. É a única receita do módulo — ver a rota. */
  readonly receitaAprovada: number;
  readonly horasReconhecidas: number;
  readonly emAberto: number;
  /** Aprovado e ainda sem documento emitido: o que falta faturar. */
  readonly semDocumento: number;
  /** Emitido e ainda não recebido. */
  readonly aFaturar: number;
}

/**
 * Os totais da faixa.
 *
 * A soma percorre a lista UMA vez por campo de propósito separado: o risco do
 * módulo não é desempenho, é somar o que não se soma. `receitaAprovada` filtra
 * `aprovada` antes de somar; se ela somasse a lista inteira, a medição em
 * aberto entraria no faturamento do mês e o número sairia plausível.
 */
export function totaisDaReceita(medicoes: readonly Medicao[]): TotaisDaReceita {
  const aprovadas = medicoes.filter((m) => m.situacao === 'aprovada');
  return {
    receitaAprovada: aprovadas.reduce((s, m) => s + m.valor, 0),
    horasReconhecidas: aprovadas.reduce((s, m) => s + m.horasReconhecidas, 0),
    emAberto: medicoes.filter((m) => m.situacao === 'aberta' || m.situacao === 'em conferencia').length,
    semDocumento: aprovadas.filter((m) => m.documento === null).reduce((s, m) => s + m.valor, 0),
    aFaturar: aprovadas.filter((m) => m.documento !== null && m.recebidoEm === null).reduce((s, m) => s + m.valor, 0),
  };
}

export type EstadoDaFaixa = 'carregando' | 'falhou' | 'pronta';

export function estadoDaFaixa(consulta: { readonly data: unknown; readonly isError: boolean }): EstadoDaFaixa {
  if (consulta.isError) return 'falhou';
  return consulta.data === undefined ? 'carregando' : 'pronta';
}

/**
 * A frase que acompanha o total.
 *
 * O v6 escreve o risco do módulo 14 em uma linha, e ele é de CONTAGEM:
 * "considerar medição, nota e recebimento como três receitas". A frase diz, em
 * toda leitura, de onde o número vem e o que ele não é — porque somar os três
 * triplicaria o faturamento e o resultado continuaria parecendo certo.
 */
export function deOndeVemONumero(t: TotaisDaReceita): string {
  const partes = ['Receita das medições aprovadas — a nota e o recebimento são o mesmo dinheiro, não receita nova'];
  if (t.semDocumento > 0) partes.push('há medição aprovada ainda sem documento emitido');
  if (t.emAberto > 0) {
    partes.push(
      t.emAberto === 1
        ? '1 medição em aberto fica fora deste total até ser aprovada'
        : `${String(t.emAberto)} medições em aberto ficam fora deste total até serem aprovadas`,
    );
  }
  return partes.join(' · ');
}

/**
 * O que dá para fazer com esta medição agora, e o motivo quando não dá.
 *
 * O motivo viaja JUNTO: ação desligada sem explicação manda a pessoa procurar
 * a permissão que falta num lugar que a tela não diz.
 */
export function porqueNaoDa(
  medicao: Medicao,
  acao: 'aprovar' | 'cancelar' | 'emitir' | 'receber',
  permissoes: readonly string[],
): string | undefined {
  const licenca: Record<typeof acao, string> = {
    aprovar: 'medicao.aprovar',
    cancelar: 'medicao.aprovar',
    emitir: 'documento.emitir',
    receber: 'documento.receber',
  };
  if (!permissoes.includes(licenca[acao])) {
    return `Você não tem a licença ${licenca[acao]}. Peça a liberação em Cadastros · Governança.`;
  }
  if (medicao.situacao === 'cancelada') return 'Medição cancelada não volta atrás.';

  if (acao === 'aprovar') {
    if (medicao.situacao === 'aprovada') return 'Esta medição já foi aprovada.';
    if (medicao.equipamentos === 0) return 'A medição não tem nenhum equipamento. Monte-a outra vez depois de conciliar as horas.';
    return undefined;
  }
  if (acao === 'cancelar') return undefined;
  if (acao === 'emitir') {
    if (medicao.situacao !== 'aprovada') return 'O documento sai de uma medição aprovada. Aprove a medição antes de emitir.';
    if (medicao.documento !== null) return `O documento ${medicao.documento} já foi emitido para esta medição.`;
    return undefined;
  }
  if (medicao.documento === null) return 'Emita o documento antes de registrar o recebimento.';
  if (medicao.recebidoEm !== null) return 'O recebimento desta medição já foi registrado.';
  return undefined;
}

/**
 * O CONTRATO A MEDIR (31/10/2026): a janela "Montar medição" abre com o
 * contrato que já foi medido antes e ainda não tem medição viva na competência
 * escolhida — o mais recentemente medido primeiro. Quem fecha o mês monta os
 * contratos um a um, e a janela propõe o próximo. Sem nenhum, o campo fica
 * para a pessoa escolher, como antes.
 */
export function contratoAMedir(medicoes: readonly Medicao[], competencia: string): { readonly contratoId: string; readonly ultimaCompetencia: string } | null {
  const ultima = new Map<string, string>();
  const jaMedidos = new Set<string>();
  for (const m of medicoes) {
    if (m.situacao === 'cancelada') continue;
    if (m.competencia === competencia) jaMedidos.add(m.contratoId);
    const antes = ultima.get(m.contratoId);
    if (antes === undefined || m.competencia > antes) ultima.set(m.contratoId, m.competencia);
  }
  const candidatos = [...ultima].filter(([id]) => !jaMedidos.has(id)).sort((a, b) => b[1].localeCompare(a[1]) || a[0].localeCompare(b[0]));
  const primeiro = candidatos[0];
  return primeiro ? { contratoId: primeiro[0], ultimaCompetencia: primeiro[1] } : null;
}

/** O valor da medição como o campo o guarda: `1.234,50`, o que entra quando o cliente paga o boletim inteiro. */
export function valorDoCampo(valor: number): string {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
